import { useInfiniteAxiosQuery } from 'hooks/useInfiniteAxiosQuery';
import { useQueryParams } from 'hooks/useQueryParams';
import { useEffect } from 'react';
import { Blueprint } from 'types';

type InfiniteQueryData = {
  accumulatedData: Blueprint[];
  perPageData: Blueprint[];
};

type UseGetBlueprintsProps = {
  perPage?: number;
  orderBy?: string;
  order?: 'asc' | 'desc';
  enabled?: boolean;
};

export const useGetBlueprints = ({
  perPage = 20,
  orderBy = 'name',
  order = 'desc',
  enabled = true,
}: UseGetBlueprintsProps = {}) => {
  const { queryKey, queryUrl, paramsChanged, resetParamsChanged } = useQueryParams({
    key: ['blueprints'],
    baseUrl: '/blueprints/',
    additionalQueryParams: [
      { key: 'order_by', value: orderBy },
      { key: 'order', value: order },
    ],
  });

  const {
    data: blueprints,
    isSuccess,
    isError,
    isLoading,
    isFetching,
    hasNextPage,
    goToNextPage,
    resetPage,
  } = useInfiniteAxiosQuery<Blueprint>({
    key: queryKey,
    path: queryUrl,
    perPage,
    accumulateData: true,
    reactQueryConfig: {
      enabled,
    },
  }) as { data: InfiniteQueryData } & Omit<ReturnType<typeof useInfiniteAxiosQuery>, 'data'>;

  useEffect(() => {
    if (paramsChanged) {
      resetPage();
      resetParamsChanged();
    }
  }, [paramsChanged, resetPage, resetParamsChanged]);

  return {
    blueprints: blueprints?.accumulatedData ?? [],
    isSuccess,
    isError,
    isLoading,
    isFetching,
    hasNextPage,
    goToNextPage,
  };
};
